import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './PaidAmountSummary.css';
import fetchData from '../../functions/fetchData';
import CardComponent from '../../components/cards/CardComponent';
import FloatingNavModal from '../../components/floatingbuttons/FloatingNavModal';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { 
  FaDollarSign, 
  FaSpinner,
  FaUser,
  FaCalendarAlt,
  FaChartPie,
  FaFilePdf,
  FaFileExcel,
  FaTable,
  FaPrint
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import logo from '../../assets/logo.png';

const PaidAmountSummary = () => {
  const navigate = useNavigate();
  
  const [summaryData, setSummaryData] = useState({
    totalPaidAmount: 0,
    totalRecords: 0,
    totalCash: 0,
    totalCard: 0,
    totalTransfer: 0,
    totalDiscount: 0,
    cashCount: 0,
    cardCount: 0,
    transferCount: 0,
    dailySummary: [],
    monthlySummary: [],
    userSummary: [],
    topPaymentMethods: []
  });
  const [detailedRecords, setDetailedRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  // Get user information for authentication
  const userType = (localStorage.getItem('usertype') || sessionStorage.getItem('usertype') || '').toLowerCase();
  const userId = localStorage.getItem('userid') || sessionStorage.getItem('userid');
  const userName = localStorage.getItem('uname') || sessionStorage.getItem('uname');
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  
  const isAuthenticated = !!token;
  const isCashier = userType === 'cashier' || userType === 'admin';

  // Fetch comprehensive summary data
  const fetchSummaryData = useCallback(async () => {
    setLoading(true);
    try {
      // Validate userId before making the request
      if (!userId) {
      // console.error('User ID is missing');
        toast.error('User authentication error. Please login again.');
        navigate('/');
        return;
      }

      // console.log('User authentication details:', {
      //   userId,
      //   userType,
      //   token: token ? 'Present' : 'Missing',
      //   isCashier
      // });
      
      // Fetch paid records where userid=uname and balance=0
      const whereCondition = { 
        balance: '0',
        userid: userName // Use uname from localStorage as userid filter
      };
      
      // console.log('Calling fetchData with params:', {
      //   table: 'collection',
      //   orderBy: 'id',
      //   where: whereCondition
      // });
      
      // Use fetchData for records where balance = 0 and userid = uname
      const paidRecords = await fetchData('collection', null, 'id', whereCondition);
      
      // console.log('Received paid data:', paidRecords);
      // console.log('Paid records count:', paidRecords?.length || 0);
      // console.log('Sample paid record:', paidRecords?.[0]);
      
      if (paidRecords && paidRecords.length > 0) {
        // Since we already filtered by userid=userName in the API call, we don't need additional filtering
        console.log('Processing paid records:', paidRecords.length);
        console.log('First record structure:', paidRecords[0]);
        console.log('All field names in first record:', Object.keys(paidRecords[0]));
        
        const summary = paidRecords.reduce((acc, record) => {
          const mode = (record.mode || '').toLowerCase().trim();
          let cashAmount = parseFloat(record.paidamount) || 0;
          let cardAmount = parseFloat(record.card) || 0;
          let transferAmount = parseFloat(record.btransfer) || 0;
          const discount = parseFloat(record.discount) || 0;
          const recordDate = record.dte;
          const recordUserId = record.userid;
          
          // If payment mode is 'card', add paidamount to card amount
          if (mode === 'card' || mode.includes('card') || mode === 'credit card' || mode === 'creditcard') {
            cardAmount += parseFloat(record.paidamount) || 0;
            cashAmount = 0; // Don't count as cash
          }
          
          // If payment mode is 'btransfer', add paidamount to transfer amount
          if (mode === 'btransfer' || mode.includes('transfer') || mode === 'bank transfer') {
            transferAmount += parseFloat(record.paidamount) || 0;
            cashAmount = 0; // Don't count as cash
          }
          
          // Calculate total paid amount from all payment columns
          const totalRecordAmount = cashAmount + cardAmount + transferAmount;
          
          console.log('Processing record ID:', record.id, { 
            paidamount: record.paidamount,
            card: record.card,
            btransfer: record.btransfer,
            cashAmount, 
            cardAmount, 
            transferAmount, 
            mode, 
            recordDate, 
            recordUserId 
          });
          
          acc.totalPaidAmount += totalRecordAmount;
          acc.totalDiscount += discount;
          acc.totalRecords += 1;
          
          // Payment mode summary - accumulate all payment types present in the record
          // A record can have multiple payment types (e.g., partial cash + partial card)
          if (cashAmount > 0) {
            acc.totalCash += cashAmount;
            acc.cashCount += 1;
          }
          if (cardAmount > 0) {
            acc.totalCard += cardAmount;
            acc.cardCount += 1;
          }
          if (transferAmount > 0) {
            acc.totalTransfer += transferAmount;
            acc.transferCount += 1;
          }
          
          // Daily summary
          const existingDay = acc.dailySummary.find(d => d.date === recordDate);
          if (existingDay) {
            existingDay.amount += totalRecordAmount;
            existingDay.count += 1;
          } else {
            acc.dailySummary.push({
              date: recordDate,
              amount: totalRecordAmount,
              count: 1
            });
          }
          
          // User summary - since userid is already uname, use it directly
          const actualUserName = recordUserId || `User ${recordUserId}`;
          const existingUser = acc.userSummary.find(u => u.userId === recordUserId);
          if (existingUser) {
            existingUser.amount += totalRecordAmount;
            existingUser.count += 1;
          } else {
            acc.userSummary.push({
              userId: recordUserId,
              userName: actualUserName,
              amount: totalRecordAmount,
              count: 1
            });
          }
          
          return acc;
        }, {
          totalPaidAmount: 0,
          totalRecords: 0,
          totalCash: 0,
          totalCard: 0,
          totalTransfer: 0,
          totalDiscount: 0,
          cashCount: 0,
          cardCount: 0,
          transferCount: 0,
          dailySummary: [],
          userSummary: []
        });
        
        // Sort daily summary by date
        summary.dailySummary.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        // Sort user summary by amount
        summary.userSummary.sort((a, b) => b.amount - a.amount);
        
        // Create top payment methods
        summary.topPaymentMethods = [
          { method: 'Cash', amount: summary.totalCash, count: summary.cashCount, color: '#28a745' },
          { method: 'Card', amount: summary.totalCard, count: summary.cardCount, color: '#007bff' },
          { method: 'Bank Transfer', amount: summary.totalTransfer, count: summary.transferCount, color: '#17a2b8' }
        ].sort((a, b) => b.amount - a.amount);
        
        console.log('=== FINAL SUMMARY ===');
        console.log('Total Cash:', summary.totalCash);
        console.log('Total Card:', summary.totalCard);
        console.log('Total Transfer:', summary.totalTransfer);
        console.log('Cash Count:', summary.cashCount);
        console.log('Card Count:', summary.cardCount);
        console.log('Transfer Count:', summary.transferCount);
        console.log('===================');
        
        setSummaryData(summary);
        setDetailedRecords(paidRecords); // Store detailed records for table
        setFilteredRecords(paidRecords); // Initialize filtered records
        // console.log('Summary data set successfully:', summary);
        // console.log('Detailed records stored:', paidRecords.length);
      } else {
        // console.log('No paid records found');
        setSummaryData({
          totalPaidAmount: 0,
          totalRecords: 0,
          totalCash: 0,
          totalCard: 0,
          totalTransfer: 0,
          totalDiscount: 0,
          cashCount: 0,
          cardCount: 0,
          transferCount: 0,
          dailySummary: [],
          userSummary: [],
          topPaymentMethods: []
        });
        setFilteredRecords([]);
      }
    } catch (error) {
      // console.error('Error fetching summary data:', error);
      // console.error('Error details:', {
      //   message: error.message,
      //   stack: error.stack,
      //   userId,
      //   whereCondition: { balance: '0' }
      // });
      
      // Provide more specific error messages
      if (error.message?.includes('500')) {
        toast.error('Server error: Please check your connection and try again.');
      } else if (error.message?.includes('401') || error.message?.includes('403')) {
        toast.error('Authentication error: Please login again.');
        navigate('/login');
      } else {
        toast.error(`Failed to fetch summary data: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  }, [userId, navigate, userName]);

  // Filter records based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredRecords(detailedRecords);
    } else {
      const filtered = detailedRecords.filter(record => 
        (record.barbill && record.barbill.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
        (record.dte && record.dte.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
        (record.tables && record.tables.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
        (record.userid && record.userid.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
        (record.mode && record.mode.toString().toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredRecords(filtered);
    }
  }, [searchTerm, detailedRecords]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(indexOfFirstRecord, indexOfLastRecord);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Export to PDF function
  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Add soft blue header background
    doc.setFillColor(230, 240, 255);
    doc.rect(0, 0, pageWidth, 30, 'F');
    
    // Add border
    doc.setDrawColor(180, 200, 230);
    doc.setLineWidth(0.5);
    doc.rect(0, 0, pageWidth, 30, 'S');
    
    // Add logo
    const imgWidth = 35;
    const imgHeight = 15;
    doc.addImage(logo, 'PNG', 14, 7, imgWidth, imgHeight);
    
    // Add title
    doc.setTextColor(40, 80, 150);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Paid Amount Summary Report', pageWidth / 2, 15, { align: 'center' });
    
    // Add generated date
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    doc.setFont(undefined, 'normal');
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth / 2, 23, { align: 'center' });
    
    // Reset text color
    doc.setTextColor(60, 60, 60);
    
    // Add summary info
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text(`Total Records: ${summaryData.totalRecords}`, 20, 40);
    doc.text(`Total Paid Amount: ฿${summaryData.totalPaidAmount.toLocaleString()}`, 20, 48);
    
    // Prepare data for table
    const tableData = detailedRecords.map((record, index) => [
      index + 1,
      record.barbill || 'N/A',
      record.dte || 'N/A',
      record.tme || 'N/A',
      record.userid || 'N/A',
      record.mode || 'N/A',
      `฿${parseFloat(record.paidamount || 0).toLocaleString()}`,
      `฿${parseFloat(record.netamount || 0).toLocaleString()}`,
      `฿${parseFloat(record.balance || 0).toLocaleString()}`
    ]);
    
    // Add table
    doc.autoTable({
      startY: 55,
      head: [['S.No', 'Bill No', 'Date', 'Time', 'User', 'Mode', 'Paid Amount', 'Net Amount', 'Balance']],
      body: tableData,
      styles: { fontSize: 8 },
      headStyles: { 
        fillColor: [200, 220, 240],
        textColor: [40, 60, 100],
        fontStyle: 'bold'
      },
      margin: { top: 55 }
    });
    
    doc.save(`paid-amount-summary-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success('PDF exported successfully!');
  };
  
  // Export to Excel function
  const exportToExcel = () => {
    const worksheetData = [
      ['Paid Amount Summary Report'],
      [`Generated on: ${new Date().toLocaleDateString()}`],
      [`Total Records: ${summaryData.totalRecords}`],
      [`Total Paid Amount: ฿${summaryData.totalPaidAmount.toLocaleString()}`],
      [],
      ['S.No', 'Bill No', 'Date', 'Time', 'User', 'Mode', 'Paid Amount', 'Net Amount', 'Balance'],
      ...detailedRecords.map((record, index) => [
        index + 1,
        record.barbill || 'N/A',
        record.dte || 'N/A',
        record.tme || 'N/A',
        record.userid || 'N/A',
        record.mode || 'N/A',
        parseFloat(record.paidamount || 0),
        parseFloat(record.netamount || 0),
        parseFloat(record.balance || 0)
      ])
    ];
    
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Paid Amount Summary');
    
    XLSX.writeFile(workbook, `paid-amount-summary-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Excel exported successfully!');
  };

  // Generate Thermal Print
  const generateThermalPrint = () => {
    try {
      const thermalContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Paid Amount Summary - Thermal Print</title>
        <style>
          @media print {
            @page { margin: 0; size: 80mm auto; }
            body { margin: 0; }
          }
          body {
            font-family: monospace;
            font-size: 11px;
            line-height: 1.2;
            margin: 0;
            padding: 5mm;
            width: 70mm;
          }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .divider { border-bottom: 1px dashed #000; margin: 5px 0; }
          .row { display: flex; justify-content: space-between; }
          .small { font-size: 9px; }
          .large { font-size: 13px; }
          .section-header {
            background: #f0f0f0;
            padding: 2px;
            margin: 5px 0;
            text-align: center;
            font-weight: bold;
          }
        </style>
      </head>
      <body>
        <div class="center bold large">JANNAAT CLUB</div>
        <div class="center small">Paid Amount Summary</div>
        <div class="divider"></div>
        
        <div class="row">
          <span>Date:</span>
          <span>${new Date().toLocaleDateString()}</span>
        </div>
        <div class="row">
          <span>Time:</span>
          <span>${new Date().toLocaleTimeString()}</span>
        </div>
        <div class="row">
          <span>Cashier:</span>
          <span>${userName || 'N/A'}</span>
        </div>
        
        <div class="divider"></div>
        <div class="section-header">PAYMENT SUMMARY</div>
        
        <div class="row bold">
          <span>Total Paid:</span>
          <span>฿${summaryData.totalPaidAmount.toLocaleString()}</span>
        </div>
        <div class="row">
          <span>Total Records:</span>
          <span>${summaryData.totalRecords}</span>
        </div>
        
        <div class="divider"></div>
        <div class="section-header">PAYMENT BREAKDOWN</div>
        
        <div style="margin-bottom: 8px;">
          <div class="bold">CASH PAYMENTS</div>
          <div class="row">
            <span>Amount:</span>
            <span>฿${summaryData.totalCash.toLocaleString()}</span>
          </div>
          <div class="row small">
            <span>Count:</span>
            <span>${summaryData.cashCount} transactions</span>
          </div>
          <div style="border-bottom: 1px dotted #ccc; margin: 3px 0;"></div>
        </div>
        
        <div style="margin-bottom: 8px;">
          <div class="bold">CARD PAYMENTS</div>
          <div class="row">
            <span>Amount:</span>
            <span>฿${summaryData.totalCard.toLocaleString()}</span>
          </div>
          <div class="row small">
            <span>Count:</span>
            <span>${summaryData.cardCount} transactions</span>
          </div>
          <div style="border-bottom: 1px dotted #ccc; margin: 3px 0;"></div>
        </div>
        
        <div style="margin-bottom: 8px;">
          <div class="bold">BANK TRANSFERS</div>
          <div class="row">
            <span>Amount:</span>
            <span>฿${summaryData.totalTransfer.toLocaleString()}</span>
          </div>
          <div class="row small">
            <span>Count:</span>
            <span>${summaryData.transferCount} transactions</span>
          </div>
          <div style="border-bottom: 1px dotted #ccc; margin: 3px 0;"></div>
        </div>
        
        <div class="divider"></div>
        <div class="center bold">SUMMARY COMPLETE</div>
        <div class="divider"></div>
        
        <div class="center small">
          Report ID: PS-${userId}-${new Date().toISOString().split('T')[0]}<br>
          Generated: ${new Date().toLocaleString()}
        </div>
      </body>
      </html>
    `;
      
      // Create a new window for thermal print
      const printWindow = window.open('', '_blank', 'width=800,height=800,scrollbars=yes,resizable=yes');
      printWindow.document.write(thermalContent);
      printWindow.document.close();
      
      // Print and automatically close the dialog
      printWindow.print();
      
      // Close the print preview window after a short delay
      setTimeout(() => {
        printWindow.close();
      }, 1000);
      
      toast.success('Thermal print initiated successfully!');
      
    } catch (error) {
      // console.error('Error generating thermal print:', error);
      toast.error('Failed to generate thermal print');
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!isCashier) {
      toast.error('Access denied. Only cashiers can access this page.');
      navigate('/dashboard/admin');
      return;
    }

    fetchSummaryData();
  }, [isAuthenticated, isCashier, navigate, fetchSummaryData]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="paid-summary">
      <ToastContainer />
      
      <div className="container-fluid">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <CardComponent>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h2 className="mb-1">
                    <FaChartPie className="me-2 text-info" />
                    Paid Amount Summary
                  </h2>
                  <p className="text-muted mb-0">
                    Comprehensive overview of all completed payments
                  </p>
                </div>
              </div>
            </CardComponent>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <FaSpinner className="fa-spin fa-2x text-primary" />
            <p className="mt-2">Loading summary data...</p>
          </div>
        ) : (
          <>
            {/* Overview Cards */}
            <div className="row mb-4">
              <div className="col-lg-2 col-md-4 col-sm-6 col-12 mb-3">
                <div className="card border-0 shadow-sm h-100" style={{
                  borderRadius: '8px',
                  minHeight: '80px',
                  background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
                  borderTop: '3px solid #28a745'
                }}>
                  <div className="card-body p-2 text-center">
                    <div className="d-flex align-items-center justify-content-center mb-1">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: '#28a745',
                        color: 'white'
                      }}>
                        <FaDollarSign style={{ fontSize: '10px' }} />
                      </div>
                      <span className="fw-bold text-success" style={{ fontSize: '1.1rem' }}>
                        ฿{summaryData.totalPaidAmount.toLocaleString()}
                      </span>
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: '500' }}>
                      Total Paid Amount
                    </div>
                    <small className="text-muted" style={{ fontSize: '0.6rem' }}>
                      {summaryData.totalRecords} transactions
                    </small>
                  </div>
                </div>
              </div>

              <div className="col-lg-2 col-md-4 col-sm-6 col-12 mb-3">
                <div className="card border-0 shadow-sm h-100" style={{
                  borderRadius: '8px',
                  minHeight: '80px',
                  background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                  borderTop: '3px solid #007bff'
                }}>
                  <div className="card-body p-2 text-center">
                    <div className="d-flex align-items-center justify-content-center mb-1">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: '#007bff',
                        color: 'white'
                      }}>
                        <i className="fa fa-credit-card" style={{ fontSize: '10px' }}></i>
                      </div>
                      <span className="fw-bold text-primary" style={{ fontSize: '1.1rem' }}>
                        ฿{summaryData.totalCard.toLocaleString()}
                      </span>
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: '500' }}>
                      Card Payments
                    </div>
                    <small className="text-muted" style={{ fontSize: '0.6rem' }}>
                      {summaryData.cardCount} transactions
                    </small>
                  </div>
                </div>
              </div>

              <div className="col-lg-2 col-md-4 col-sm-6 col-12 mb-3">
                <div className="card border-0 shadow-sm h-100" style={{
                  borderRadius: '8px',
                  minHeight: '80px',
                  background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                  borderTop: '3px solid #fd7e14'
                }}>
                  <div className="card-body p-2 text-center">
                    <div className="d-flex align-items-center justify-content-center mb-1">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: '#fd7e14',
                        color: 'white'
                      }}>
                        <FaDollarSign style={{ fontSize: '10px' }} />
                      </div>
                      <span className="fw-bold" style={{ fontSize: '1.1rem', color: '#fd7e14' }}>
                        ฿{summaryData.totalCash.toLocaleString()}
                      </span>
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: '500' }}>
                      Cash Payments
                    </div>
                    <small className="text-muted" style={{ fontSize: '0.6rem' }}>
                      {summaryData.cashCount} transactions
                    </small>
                  </div>
                </div>
              </div>

              <div className="col-lg-2 col-md-4 col-sm-6 col-12 mb-3">
                <div className="card border-0 shadow-sm h-100" style={{
                  borderRadius: '8px',
                  minHeight: '80px',
                  background: 'linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)',
                  borderTop: '3px solid #17a2b8'
                }}>
                  <div className="card-body p-2 text-center">
                    <div className="d-flex align-items-center justify-content-center mb-1">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: '#17a2b8',
                        color: 'white'
                      }}>
                        <i className="fa fa-university" style={{ fontSize: '10px' }}></i>
                      </div>
                      <span className="fw-bold text-info" style={{ fontSize: '1.1rem' }}>
                        ฿{summaryData.totalTransfer.toLocaleString()}
                      </span>
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: '500' }}>
                      Bank Transfers
                    </div>
                    <small className="text-muted" style={{ fontSize: '0.6rem' }}>
                      {summaryData.transferCount} transactions
                    </small>
                  </div>
                </div>
              </div>

              <div className="col-lg-2 col-md-4 col-sm-6 col-12 mb-3">
                <div className="card border-0 shadow-sm h-100" style={{
                  borderRadius: '8px',
                  minHeight: '80px',
                  background: 'linear-gradient(135deg, #fff0f6 0%, #ffcce0 100%)',
                  borderTop: '3px solid #dc3545'
                }}>
                  <div className="card-body p-2 text-center">
                    <div className="d-flex align-items-center justify-content-center mb-1">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: '#dc3545',
                        color: 'white'
                      }}>
                        <i className="fa fa-tag" style={{ fontSize: '10px' }}></i>
                      </div>
                      <span className="fw-bold text-danger" style={{ fontSize: '1.1rem' }}>
                        ฿{summaryData.totalDiscount.toLocaleString()}
                      </span>
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: '500' }}>
                      Total Discount
                    </div>
                    <small className="text-muted" style={{ fontSize: '0.6rem' }}>
                      Applied to {summaryData.totalRecords} transactions
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed Records View */}
            <div className="row mb-4">
              <div className="col-12">
                <CardComponent>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center gap-3">
                      <h5 className="mb-0">
                        <FaTable className="me-2 text-primary" />
                        All Paid Transactions
                      </h5>
                      <div className="input-group" style={{ maxWidth: '300px' }}>
                        <span className="input-group-text">
                          <i className="fa fa-search"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search by Bill No, Date, Table, User, Mode..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && searchTerm.trim()) {
                              // After Enter key is pressed (barcode scanned), clear the input after 1 second
                              setTimeout(() => {
                                setSearchTerm('');
                              }, 1000);
                            }
                          }}
                          style={{ fontSize: '14px' }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Export buttons moved to separate row on the right */}
                  <div className="d-flex justify-content-end mb-3">
                    <div className="d-flex gap-1">
                      <button 
                        className="btn btn-danger btn-xs"
                        onClick={exportToPDF}
                        disabled={detailedRecords.length === 0}
                        style={{ fontSize: '10px', padding: '2px 6px' }}
                      >
                        <FaFilePdf className="me-1" style={{ fontSize: '10px' }} /> PDF
                      </button>
                      <button 
                        className="btn btn-success btn-xs"
                        onClick={exportToExcel}
                        disabled={detailedRecords.length === 0}
                        style={{ fontSize: '10px', padding: '2px 6px' }}
                      >
                        <FaFileExcel className="me-1" style={{ fontSize: '10px' }} /> Excel
                      </button>
                      <button 
                        className="btn btn-info btn-xs"
                        onClick={generateThermalPrint}
                        disabled={detailedRecords.length === 0}
                        style={{ fontSize: '10px', padding: '2px 6px' }}
                      >
                        <FaPrint className="me-1" style={{ fontSize: '10px' }} /> Print
                      </button>
                    </div>
                  </div>
                  
                  {/* Pagination info only */}
                  <div className="d-flex justify-content-end align-items-center mb-3">
                    <div className="text-muted small">
                      Showing {Math.min((currentPage - 1) * recordsPerPage + 1, filteredRecords.length)} to{' '}
                      {Math.min(currentPage * recordsPerPage, filteredRecords.length)} of {filteredRecords.length} entries
                      {searchTerm && ` (filtered from ${detailedRecords.length} total entries)`}
                    </div>
                  </div>
                  
                  {filteredRecords.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-muted">
                        {searchTerm ? `No transactions found matching "${searchTerm}"` : 'No paid transactions found'}
                      </p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover table-striped">
                        <thead className="table-primary">
                          <tr>
                            <th>#</th>
                            <th>Bill No</th>
                            <th>Date</th>
                            <th>Time</th>
                            <th>Table</th>
                            <th>User</th>
                            <th>Payment Mode</th>
                            <th>💰 Cash</th>
                            <th>🏦 Bank(QR)</th>
                            <th>💳 Card</th>
                            <th>💸 Discount</th>
                            <th>Net Amount</th>
                            <th>Balance</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentRecords.map((record, index) => (
                            <tr key={record.id || index}>
                              <td>{indexOfFirstRecord + index + 1}</td>
                              <td>
                                <span className="badge bg-secondary">
                                  {record.barbill || 'N/A'}
                                </span>
                              </td>
                              <td>
                                <FaCalendarAlt className="me-1 text-muted" size={12} />
                                {record.dte || 'N/A'}
                              </td>
                              <td>{record.tme || 'N/A'}</td>
                              <td>
                                <span className="badge bg-info">
                                  {record.tables || 'N/A'}
                                </span>
                              </td>
                              <td>
                                <FaUser className="me-1 text-muted" size={12} />
                                {record.userid || 'N/A'}
                              </td>
                              <td>
                                <span className={`badge ${
                                  record.mode === 'cash' ? 'bg-success' :
                                  record.mode === 'card' ? 'bg-primary' :
                                  record.mode === 'btransfer' ? 'bg-info' :
                                  'bg-secondary'
                                }`}>
                                  {(record.mode || 'N/A').toUpperCase()}
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-success">
                                  ฿{parseFloat(record.paidamount || 0).toLocaleString()}
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-info">
                                  ฿{parseFloat(record.btransfer || 0).toLocaleString()}
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-primary">
                                  ฿{parseFloat(record.card || 0).toLocaleString()}
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-warning text-dark">
                                  ฿{parseFloat(record.discount || 0).toLocaleString()}
                                </span>
                              </td>
                              <td>
                                ฿{parseFloat(record.netamount || 0).toLocaleString()}
                              </td>
                              <td className={parseFloat(record.balance || 0) > 0 ? 'text-warning' : 'text-success'}>
                                ฿{parseFloat(record.balance || 0).toLocaleString()}
                              </td>
                              <td>
                                <span className={`badge ${
                                  parseFloat(record.balance || 0) <= 0 ? 'bg-success' : 'bg-warning'
                                }`}>
                                  {parseFloat(record.balance || 0) <= 0 ? 'Paid' : 'Partial'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="table-primary">
                          <tr style={{ 
                            backgroundColor: '#0d6efd', 
                            color: 'white',
                            fontSize: '1.1rem',
                            fontWeight: 'bold'
                          }}>
                            <td colSpan="7" className="fw-bold" style={{ 
                              fontSize: '1.2rem', 
                              padding: '15px 12px',
                              backgroundColor: '#0d6efd',
                              color: 'white'
                            }}>
                              Total:
                            </td>
                            <td className="fw-bold text-white" style={{ 
                              fontSize: '1.1rem', 
                              padding: '15px 12px',
                              backgroundColor: '#0d6efd',
                              color: 'white'
                            }}>
                              ฿{filteredRecords.reduce((sum, record) => {
                                const mode = (record.mode || '').toLowerCase().trim();
                                let cashAmount = parseFloat(record.paidamount || 0);
                                // Don't count as cash if mode is card or transfer
                                if (mode === 'card' || mode.includes('card') || mode === 'credit card' || mode === 'creditcard') {
                                  cashAmount = 0;
                                }
                                if (mode === 'btransfer' || mode.includes('transfer') || mode === 'bank transfer') {
                                  cashAmount = 0;
                                }
                                return sum + cashAmount;
                              }, 0).toLocaleString()}
                            </td>
                            <td className="fw-bold text-white" style={{ 
                              fontSize: '1.1rem', 
                              padding: '15px 12px',
                              backgroundColor: '#0d6efd',
                              color: 'white'
                            }}>
                              ฿{filteredRecords.reduce((sum, record) => {
                                const mode = (record.mode || '').toLowerCase().trim();
                                let transferAmount = parseFloat(record.btransfer || 0);
                                // Add paidamount if mode is btransfer
                                if (mode === 'btransfer' || mode.includes('transfer') || mode === 'bank transfer') {
                                  transferAmount += parseFloat(record.paidamount || 0);
                                }
                                return sum + transferAmount;
                              }, 0).toLocaleString()}
                            </td>
                            <td className="fw-bold text-white" style={{ 
                              fontSize: '1.1rem', 
                              padding: '15px 12px',
                              backgroundColor: '#0d6efd',
                              color: 'white'
                            }}>
                              ฿{filteredRecords.reduce((sum, record) => {
                                const mode = (record.mode || '').toLowerCase().trim();
                                let cardAmount = parseFloat(record.card || 0);
                                // Add paidamount if mode is card
                                if (mode === 'card' || mode.includes('card') || mode === 'credit card' || mode === 'creditcard') {
                                  cardAmount += parseFloat(record.paidamount || 0);
                                }
                                return sum + cardAmount;
                              }, 0).toLocaleString()}
                            </td>
                            <td className="fw-bold text-white" style={{ 
                              fontSize: '1.1rem', 
                              padding: '15px 12px',
                              backgroundColor: '#0d6efd',
                              color: 'white'
                            }}>
                              ฿{filteredRecords.reduce((sum, record) => sum + parseFloat(record.discount || 0), 0).toLocaleString()}
                            </td>
                            <td className="fw-bold text-white" style={{ 
                              fontSize: '1.1rem', 
                              padding: '15px 12px',
                              backgroundColor: '#0d6efd',
                              color: 'white'
                            }}>
                              ฿{filteredRecords.reduce((sum, record) => sum + parseFloat(record.netamount || 0), 0).toLocaleString()}
                            </td>
                            <td className="fw-bold text-white" style={{ 
                              fontSize: '1.1rem', 
                              padding: '15px 12px',
                              backgroundColor: '#0d6efd',
                              color: 'white'
                            }}>
                              ฿{filteredRecords.reduce((sum, record) => sum + parseFloat(record.balance || 0), 0).toLocaleString()}
                            </td>
                            <td style={{ 
                              fontSize: '1.0rem', 
                              padding: '15px 12px',
                              backgroundColor: '#0d6efd',
                              color: 'white'
                            }}>
                              <span className="badge bg-light text-primary fw-bold" style={{ fontSize: '0.9rem' }}>
                                {filteredRecords.length} Records
                              </span>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                    
                  {filteredRecords.length > 0 && totalPages > 1 && (
                    <div className="d-flex justify-content-center align-items-center mt-3">
                      <nav aria-label="Table pagination">
                        <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={currentPage === 1}
                                style={{ fontSize: '12px', padding: '4px 8px' }}
                              >
                                Previous
                              </button>
                            </li>
                            
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(pageNum => {
                                const maxVisiblePages = 5;
                                const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                                const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                                return pageNum >= startPage && pageNum <= endPage;
                              })
                              .map(pageNum => (
                                <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(pageNum)}
                                    style={{ fontSize: '12px', padding: '4px 8px' }}
                                  >
                                    {pageNum}
                                  </button>
                                </li>
                              ))
                            }
                            
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                style={{ fontSize: '12px', padding: '4px 8px' }}
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                        </nav>
                        
                        {/* Records per page selector after pagination */}
                        <div className="d-flex align-items-center gap-2 ms-4">
                          <span className="text-muted small">Show:</span>
                          <select
                            className="form-select form-select-sm"
                            value={recordsPerPage}
                            onChange={(e) => {
                              setRecordsPerPage(Number(e.target.value));
                              setCurrentPage(1);
                            }}
                            style={{ width: 'auto', fontSize: '12px' }}
                          >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={filteredRecords.length || 1}>All</option>
                          </select>
                          <span className="text-muted small">entries</span>
                        </div>
                      </div>
                    )}
                </CardComponent>
              </div>
            </div>
          </>
        )}

        {/* Floating Navigation Modal */}
        <FloatingNavModal currentPage="paid" />
      </div>
    </div>
  );
};

export default PaidAmountSummary;